import { Phone, FileText } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { trackEvent } from "../lib/analytics";

/**
 * Sticky mobile-only CTA bar pinned to the bottom of the viewport.
 *
 *  - Two equal-width buttons: "Call" (tel:) + "Quote" (/quote)
 *  - Visible only on screens <md (640px)
 *  - Hidden on routes where it conflicts with the page UI: /admin, /checkout,
 *    /success, /customize/* (customizer needs full vertical space).
 *  - Pure CSS positioning, no JS layout math → zero performance cost.
 *  - Adds bottom padding to body via a spacer so it never covers footer links.
 *
 *  Conversion tracking: clicks fire GA4 events `cta_mobile_call_click` and
 *  `cta_mobile_quote_click`.
 */
const HIDE_PREFIXES = ["/admin", "/checkout", "/success", "/customize"];

export default function MobileStickyCTA() {
  const { pathname } = useLocation();
  const hidden = HIDE_PREFIXES.some((p) => pathname.startsWith(p));
  if (hidden) return null;

  return (
    <>
      {/* Spacer so content can scroll past the bar */}
      <div className="md:hidden h-16" aria-hidden="true" />
      <div
        data-testid="mobile-sticky-cta"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-2 border-t border-[#222] bg-black/95 backdrop-blur-sm"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
      >
        <a
          href="tel:+12892053623"
          onClick={() => trackEvent("cta_mobile_call_click", { source: "sticky_bar" })}
          data-testid="mobile-sticky-call"
          className="flex items-center justify-center gap-2 py-4 text-white font-ui font-bold uppercase tracking-wider text-sm border-r border-[#222] active:bg-zinc-900"
        >
          <Phone size={16} className="text-[var(--tc-red)]" />
          Call
        </a>
        <Link
          to="/quote"
          onClick={() => trackEvent("cta_mobile_quote_click", { source: "sticky_bar" })}
          data-testid="mobile-sticky-quote"
          className="flex items-center justify-center gap-2 py-4 bg-[var(--tc-red)] text-white font-ui font-bold uppercase tracking-wider text-sm active:opacity-80"
        >
          <FileText size={16} />
          Get Quote
        </Link>
      </div>
    </>
  );
}
